<template>
  <div class="role-form-container">
    <el-card :title="isEdit ? '编辑角色' : '添加角色'">
      <el-form ref="roleForm" :model="roleForm" :rules="rules" label-width="100px">
        <el-form-item label="角色名称" prop="name">
          <el-input v-model="roleForm.name" placeholder="请输入角色名称"></el-input>
        </el-form-item>
        
        <!-- 所属作品 - 修复对齐问题 -->
        <el-form-item label="所属作品" prop="workId">
          <el-select 
            v-model="roleForm.workId" 
            placeholder="选择作品"
            filterable
            class="form-control">  <!-- 添加统一控制类 -->
            <el-option label="所有作品" value=""></el-option>
            <el-option 
              v-for="work in works" 
              :key="work.id" 
              :label="work.name" 
              :value="work.id">
            </el-option>
          </el-select>
        </el-form-item>
        
        <el-form-item label="角色图片">
          <el-upload
            class="upload-demo"
            action=""
            :auto-upload="false"
            :on-change="handleFileSelect"
            :show-file-list="false"
            accept="image/jpeg,image/png">
            <el-button size="small" type="primary">点击上传</el-button>
          </el-upload>
          
          <div v-if="localCoverPreview || roleForm.imageUrl">
            <el-image 
              v-if ="localCoverPreview" 
              :src="localCoverPreview"  
              style="width: 150px; margin-top: 10px;" 
              fit="contain">
            </el-image>
            <el-image 
              v-else-if="roleForm.imageUrl" 
              :src="roleForm.imageUrl" 
              style="width: 150px; margin-top: 10px"
              fit="contain">
            </el-image>
            <div class="preview-actions">
              <el-button 
                type="text" 
                icon="el-icon-refresh-left" 
                @click="resetLocalPreview">
                重新选择
              </el-button>
            </div>
          </div>
        </el-form-item>
        
        <el-form-item>
          <el-button @click="resetForm">取消</el-button>
          <el-button type="primary" @click="submitForm" :loading="submitting">提交</el-button>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import { createRole, updateRole, getRoleDetail } from '@/api/role';
import { getAllWorks } from '@/api/work';
import { uploadImage } from '@/api/upload';

export default {
  data() {
    return {
      roleForm: {
        name: '',
        workId: null,
        imageUrl: ''
      },
      rules: {
        name: [
          { required: true, message: '请输入角色名称', trigger: 'blur' }
        ],
        workId: [
          { required: true, message: '请选择作品', trigger: 'change' }
        ]
      },
      works: [],
      uploadUrl: process.env.VUE_APP_BASE_API + '/upload/image',
      submitting: false,
      isEdit: false,
      roleId: null,
      localCoverPreview: null,  // 本地预览URL
      fileToUpload: null,       // 待上传的文件对象
    };
  },
  
  created() {
    // 判断是否为编辑模式
    // 检查是否为编辑模式
    this.roleId = this.$route.query.id;
    this.isEdit = !!this.roleId;
    
    // 加载作品列表
    this.loadAllWorks();
    
    // 如果是编辑模式，加载角色详情
    if (this.isEdit) {
      this.loadRoleDetail();
    } else {
      // 添加模式下检查URL中的作品ID参数
      const workId = this.$route.query.workId;
      if (workId) {
        this.roleForm.workId = Number(workId);
      }
    }
  },
  
  methods: {
    // 加载角色详情（编辑模式）
    async loadRoleDetail() {
      try {
        const response = await getRoleDetail(this.roleId);
        const roleData = response.data.data;
        
        // 填充表单数据
        this.roleForm = {
          name: roleData.name || '',
          workId: roleData.workId || null,
          imageUrl: roleData.imageUrl || ''
        };
        console.log('角色详情加载成功', this.roleForm);
      } catch (error) {
        console.error('加载角色详情失败', error);
        this.$message.error('加载角色数据失败');
        this.$router.go(-1);
      }
    },
    
    async loadAllWorks() {
      this.loadingWorks = true;
      try {
        const response = await getAllWorks();
        this.works = Array.isArray(response.data.data) ? response.data.data : [];
        
        // 作品列表加载完成后设置默认值
        this.setDefaultWorkId();
      } catch (error) {
        console.error('加载作品失败:', error);
        this.$message.error('加载作品失败');
      } finally {
        this.loadingWorks = false;
      }
    },

    // 单独的方法设置默认作品ID
    setDefaultWorkId() {
      const workId = this.$route.query.workId;
      if (workId && this.works.length > 0) {
        // 将URL参数转换为数字类型，确保与选项值类型一致
        const numericWorkId = Number(workId);
        this.roleForm.workId = numericWorkId;
        
        // 验证是否找到匹配的作品
        const found = this.works.some(work => work.id === numericWorkId);
        if (!found) {
          console.warn(`未找到ID为${numericWorkId}的作品`);
          this.$message.warning('关联作品不存在，已重置选择');
          this.roleForm.workId = null;
        }
      }
    },
    
    beforeUpload(file) {
      const isJPG = file.type === 'image/jpeg' || file.type === 'image/png';
      const isLt2M = file.size / 1024 / 1024 < 5;
      
      if (!isJPG) this.$message.error('只能上传JPG/PNG图片');
      if (!isLt2M) this.$message.error('图片大小不能超过5MB');
      
      return isJPG && isLt2M;
    },
    
    handleImageUploadSuccess(response) {
      if (response.data && response.data.url) {
        this.roleForm.imageUrl = response.data.url;
      }
    },

    // 处理文件选择（使用el-upload的on-change事件）
    handleFileSelect(file) {
      console.log('文件选择事件触发', file);
      
      const rawFile = file.raw;
      
      // 验证文件类型
      if (!['image/jpeg', 'image/png'].includes(rawFile.type)) {
        this.$message.error('只能上传JPG/PNG格式的图片');
        return;
      }
      
      // 验证文件大小
      if (rawFile.size > 5 * 1024 * 1024) {
        this.$message.error('图片大小不能超过2MB');
        return;
      }
      
      // 本地预览
      const reader = new FileReader();
      reader.onload = (e) => {
        this.localCoverPreview = e.target.result;
        this.fileToUpload = rawFile;
        console.log('本地预览生成成功');
      };
      reader.readAsDataURL(rawFile);
    },
    
    async submitForm() {
      this.$refs.roleForm.validate(async (valid) => {
        if (valid) {
          this.submitting = true;
          // 验证文件对象
          if (!this.fileToUpload || !(this.fileToUpload instanceof File)) {
            throw new Error('无效的文件对象，请重新选择图片');
          }
          
          // 调用上传API，传递原始文件对象
          const uploadResponse = await uploadImage(this.fileToUpload);
          
          if (!uploadResponse.data || !uploadResponse.data.data.url) {
            throw new Error('上传失败: 未返回图片URL');
          }
          this.roleForm.imageUrl = uploadResponse.data.data.url
          try {
            if (this.isEdit) {
              await updateRole(this.roleId, this.roleForm);
              this.$message.success('角色更新成功');
            } else {
              await createRole(this.roleForm);
              this.$message.success('角色创建成功');
            }
            this.$router.push('/roles');
          } catch (error) {
            console.error('更新失败:', error);
            this.$message.error(this.isEdit ? '更新失败' : '创建失败');
          } finally {
            this.submitting = false;
          }
        }
      });
    },
    
    resetForm() {
      this.$refs.roleForm.resetFields();
      this.$router.go(-1);
    },

    // 重置本地预览
    resetLocalPreview() {
      this.localCoverPreview = null;
      this.fileToUpload = null;
      // this.resetFileInput();
    },
  }
};
</script>
<style scoped>

.preview-actions {
  margin-top: 10px;
  text-align: center;
}
</style>